import { Component, Input } from '@angular/core';
import { CopilotInsightContent, CopilotInsightEvidence, InsightChurnRisk, InsightFact, InsightFactKind, InsightHealthBand } from 'lib-trend-core';
import { timeLabel } from '../attendance-copilot-insight/insight-time.util';
import { badgeTone, healthBarClass, riskKey } from '../attendance-copilot-insight/insight-tones.util';

export type InsightSection = 'situation' | 'questions' | 'needs' | 'relationship' | 'attention' | 'opportunities' | 'facts';

const META_KEYS = 'attendance.copilot.insight.meta.';

const FACT_DOTS: Record<InsightFactKind, string> = {
  attention: 'bg-warning',
  progress: 'bg-success',
  neutral: 'bg-secondary opacity-50',
};

/**
 * The content of an insight as an accordion (SPEC-2026-10-copilot-attendant-insight, RF-18 to
 * RF-21; prototype D): every section in the order of RF-18, one open at a time, each title
 * carrying what the section holds. A section with nothing to report says so.
 */
@Component({
  selector: 'attendance-copilot-insight-content',
  templateUrl: './attendance-copilot-insight-content.component.html',
  styleUrls: ['./attendance-copilot-insight-content.component.scss'],
  standalone: false,
})
export class AttendanceCopilotInsightContentComponent {
  @Input({ required: true }) content!: CopilotInsightContent;
  @Input() timezone = '';

  /** The situation opens first; the others open on request, one at a time. */
  open: InsightSection | null = 'situation';

  isOpen(section: InsightSection): boolean {
    return this.open === section;
  }

  toggle(section: InsightSection): void {
    this.open = this.isOpen(section) ? null : section;
  }

  chevron(section: InsightSection): string {
    return this.isOpen(section) ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line';
  }

  tone(value: string): string {
    return badgeTone(value);
  }

  /** A count in a section title: attention-coloured while there is something to read. */
  countTone(count: number): string {
    return count > 0 ? 'is-warn' : 'is-neutral';
  }

  pluralKey(base: 'implicitNeeds' | 'pains' | 'facts', count: number): string {
    return META_KEYS + base + (count === 1 ? 'One' : 'Many');
  }

  riskMetaKey(level: InsightChurnRisk): string {
    return riskKey(META_KEYS, level);
  }

  healthBar(band: InsightHealthBand): string {
    return healthBarClass(band);
  }

  factDot(kind: InsightFactKind): string {
    return FACT_DOTS[kind] ?? FACT_DOTS.neutral;
  }

  /** The opportunities beyond the first one, whose type the title shows. */
  moreOpportunities(): number {
    return Math.max(0, this.content.opportunities.length - 1);
  }

  /** The time of an evidence, with its date when it is not from today (RF-21). */
  when(evidence: CopilotInsightEvidence): string {
    return timeLabel(evidence.at, this.timezone);
  }

  /** A fact's time is the time of its message, the first evidence it cites (RF-18). */
  factTime(fact: InsightFact): string {
    const first = fact.evidence[0];
    return first ? this.when(first) : '';
  }
}
